import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, UserPlus, KeyRound, Trash2, Users, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { OFFICE, OfficeCard, OfficeButton, OfficeField, OfficeInput } from "@/pages/admin/officeUi";

function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [next2, setNext2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("La nuova password deve avere almeno 8 caratteri"); return; }
    if (next !== next2) { toast.error("Le due password non coincidono"); return; }
    setBusy(true);
    try {
      await api.changeOwnPassword(current, next);
      toast.success("Password aggiornata");
      setCurrent(""); setNext(""); setNext2("");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <OfficeCard className="mt-6">
      <h2 className="font-display text-lg mb-1">La tua password</h2>
      <p className="text-sm mb-4" style={{ color: OFFICE.muted }}>Cambia la password del tuo account.</p>
      <form onSubmit={submit} className="space-y-4">
        <OfficeField label="Password attuale"><OfficeInput type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" /></OfficeField>
        <OfficeField label="Nuova password" hint="Almeno 8 caratteri"><OfficeInput type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" /></OfficeField>
        <OfficeField label="Ripeti nuova password"><OfficeInput type="password" value={next2} onChange={(e) => setNext2(e.target.value)} autoComplete="new-password" /></OfficeField>
        <div className="pt-2"><OfficeButton type="submit" disabled={busy}>{busy ? "Salvataggio…" : "Aggiorna password"}</OfficeButton></div>
      </form>
    </OfficeCard>
  );
}

function TeamSection() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetFor, setResetFor] = useState({}); // uid -> pw string

  const load = useCallback(async () => {
    try { setTeam(await api.listTeam()); } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!email.trim() || pw.length < 8) { toast.error("Email valida e password (min 8 caratteri)"); return; }
    setBusy(true);
    try {
      const res = await api.createTeamMember(email.trim(), pw);
      toast.success(res.email_sent ? "Collaboratore creato · email di benvenuto inviata" : "Collaboratore creato (email non inviata)");
      setEmail(""); setPw(""); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const resetPw = async (uid) => {
    const p = resetFor[uid] || "";
    if (p.length < 8) { toast.error("Password minimo 8 caratteri"); return; }
    try { const r = await api.resetTeamPassword(uid, p); toast.success(r.email_sent ? "Password reimpostata · email inviata" : "Password reimpostata"); setResetFor((s) => ({ ...s, [uid]: undefined, [`open_${uid}`]: false })); }
    catch (e) { toast.error(e.message); }
  };

  const remove = async (uid) => {
    if (!window.confirm("Rimuovere questo collaboratore dal team?")) return;
    try { await api.deleteTeamMember(uid); toast.success("Collaboratore rimosso"); load(); } catch (e) { toast.error(e.message); }
  };

  return (
    <OfficeCard className="mt-6">
      <div className="flex items-center gap-2 mb-1">
        <Users className="h-5 w-5" style={{ color: OFFICE.brand }} />
        <h2 className="font-display text-lg">Il tuo team</h2>
      </div>
      <p className="text-sm mb-4" style={{ color: OFFICE.muted }}>
        Crea accessi per i collaboratori del team. Avranno gli stessi permessi di amministratore (strutture, dati, accessi clienti).
      </p>

      <div className="space-y-3">
        {loading ? <p style={{ color: OFFICE.muted }}>Caricamento…</p> :
          team.map((m) => (
            <div key={m.id} className="rounded-xl border p-4" style={{ borderColor: OFFICE.border }}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" style={{ color: OFFICE.brand }} />
                  <span className="font-medium" style={{ color: OFFICE.ink }}>{m.email}</span>
                  {m.is_self ? <span className="text-xs rounded-full px-2 py-0.5" style={{ background: OFFICE.brandTertiary, color: OFFICE.brandStrong }}>tu</span> : null}
                </div>
                {!m.is_self ? (
                  <div className="flex items-center gap-2">
                    <OfficeButton variant="ghost" className="!py-1.5 !px-3" onClick={() => setResetFor((s) => ({ ...s, [`open_${m.id}`]: !s[`open_${m.id}`] }))}><KeyRound className="h-4 w-4" /> Reset password</OfficeButton>
                    <OfficeButton variant="danger" className="!py-1.5 !px-3" onClick={() => remove(m.id)}><Trash2 className="h-4 w-4" /> Rimuovi</OfficeButton>
                  </div>
                ) : null}
              </div>
              {resetFor[`open_${m.id}`] ? (
                <div className="mt-3 flex gap-2">
                  <OfficeInput type="text" value={resetFor[m.id] || ""} onChange={(e) => setResetFor((s) => ({ ...s, [m.id]: e.target.value }))} placeholder="Nuova password (min 8)" />
                  <OfficeButton onClick={() => resetPw(m.id)}>Applica</OfficeButton>
                </div>
              ) : null}
            </div>
          ))}
      </div>

      <div className="mt-5 rounded-xl border p-4" style={{ borderColor: OFFICE.border, background: OFFICE.surface3 }}>
        <div className="flex items-center gap-2 mb-3"><UserPlus className="h-4 w-4" style={{ color: OFFICE.brand }} /><span className="font-medium" style={{ color: OFFICE.ink }}>Nuovo collaboratore</span></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <OfficeInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@collaboratore.it" />
          <OfficeInput type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password iniziale (min 8)" />
          <OfficeButton onClick={create} disabled={busy}><UserPlus className="h-4 w-4" /> {busy ? "Creazione…" : "Crea accesso"}</OfficeButton>
        </div>
        <p className="text-xs mt-2" style={{ color: OFFICE.muted }}>Il collaboratore riceve un'email di benvenuto con un link per scegliere la propria password.</p>
      </div>
    </OfficeCard>
  );
}

export default function AdminSettings() {
  const navigate = useNavigate();
  return (
    <div className="theme-office min-h-screen" style={{ background: OFFICE.surface2, color: OFFICE.ink }}>
      <header className="sticky top-0 z-30" style={{ background: OFFICE.surface, borderBottom: `1px solid ${OFFICE.border}` }}>
        <div className="max-w-6xl mx-auto px-5 md:px-6 h-16 flex items-center">
          <OfficeButton variant="ghost" onClick={() => navigate("/admin")}><ArrowLeft className="h-4 w-4" /> Torna al pannello</OfficeButton>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-5 md:px-6 py-10">
        <h1 className="font-display text-2xl">Impostazioni</h1>
        <p className="mt-1" style={{ color: OFFICE.muted }}>Gestisci il tuo account e i collaboratori del team.</p>
        <PasswordSection />
        <TeamSection />
      </main>
    </div>
  );
}
